import React, { useState, useEffect } from 'react';

const AnimatedBackground = () => {
  const [isSpectrumTheme, setIsSpectrumTheme] = useState(false);
  
  useEffect(() => {
    // Check initial theme
    setIsSpectrumTheme(document.body.classList.contains('theme-spectrum'));
    
    // Create observer to watch for theme changes
    const observer = new MutationObserver(() => {
      setIsSpectrumTheme(document.body.classList.contains('theme-spectrum'));
    });
    
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['class']
    });
    
    return () => observer.disconnect();
  }, []);
  
  return (
    <div className="fixed inset-0 -z-10 overflow-hidden">
      {/* Dark overlay for better text readability */}
      <div className="absolute inset-0 bg-black/40 z-10"></div>

      {/* Spectrum Theme Rainbow Background */}
      {isSpectrumTheme && (
        <>
          {/* Rainbow animated lines */}
          {Array.from({ length: 25 }).map((_, i) => (
            <div key={i} className="rainbow"></div>
          ))}
          {/* Light effects */}
          <div className="spectrum-light-h"></div>
          <div className="spectrum-light-v"></div>
        </>
      )}
      
      {/* Regular theme background */}
      {!isSpectrumTheme && (
        <div 
          className="absolute inset-0 animate-gradient-shift"
          style={{
            background: `linear-gradient(45deg, 
              var(--theme-gradient-start), 
              var(--theme-gradient-end), 
              var(--theme-primary), 
              var(--theme-secondary),
              var(--theme-accent),
              var(--theme-gradient-start)
            )`,
            backgroundSize: '400% 400%'
          }}
        ></div>
      )}
      
      {/* Floating geometric shapes */}
      <div className="absolute inset-0">
        {/* Large floating orbs */}
        <div className="absolute top-20 left-10 w-96 h-96 rounded-full blur-3xl animate-float-slow" 
             style={{backgroundColor: 'var(--theme-primary)', opacity: '0.1'}}></div>
        <div className="absolute top-60 right-20 w-80 h-80 rounded-full blur-3xl animate-float-medium"
             style={{backgroundColor: 'var(--theme-secondary)', opacity: '0.1'}}></div>
        <div className="absolute bottom-40 left-1/3 w-72 h-72 rounded-full blur-3xl animate-float-fast"
             style={{backgroundColor: 'var(--theme-accent)', opacity: '0.1'}}></div>
        
        {/* Smaller accent orbs */}
        <div className="absolute top-1/4 right-1/4 w-40 h-40 rounded-full blur-2xl animate-float-reverse"
             style={{backgroundColor: 'var(--theme-gradient-start)', opacity: '0.1'}}></div>
        <div className="absolute bottom-1/4 right-1/3 w-32 h-32 rounded-full blur-2xl animate-float-slow"
             style={{backgroundColor: 'var(--theme-gradient-end)', opacity: '0.1'}}></div>
        <div className="absolute top-3/4 left-1/4 w-48 h-48 rounded-full blur-2xl animate-float-medium"
             style={{backgroundColor: 'var(--theme-primary)', opacity: '0.15'}}></div>
        
        {/* Moving geometric lines */}
        <div className="absolute top-0 left-0 w-full h-full">
          <div className="absolute top-1/4 left-0 w-full h-px animate-slide-right"
               style={{background: `linear-gradient(to right, transparent, var(--theme-primary)30, transparent)`}}></div>
          <div className="absolute top-2/4 left-0 w-full h-px animate-slide-left"
               style={{background: `linear-gradient(to right, transparent, var(--theme-secondary)30, transparent)`}}></div>
          <div className="absolute top-3/4 left-0 w-full h-px animate-slide-right"
               style={{background: `linear-gradient(to right, transparent, var(--theme-accent)30, transparent)`}}></div>
        </div>
        
        {/* Animated particles */}
        <div className="absolute inset-0">
          {Array.from({ length: 20 }).map((_, i) => (
            <div
              key={i}
              className={`absolute w-1 h-1 bg-white/20 rounded-full animate-particle-${i % 3 + 1}`}
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 10}s`,
              }}
            ></div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AnimatedBackground;